/**
 * Shim for `react-native-pell-rich-editor`.
 *
 * `RichEditor` is a `contentEditable` div exposing the imperative methods the
 * app calls (setContentHTML, focusContentEditor, ...). `RichToolbar` renders
 * formatting buttons that run `document.execCommand` on the focused editor.
 * `actions` maps action keys to execCommand names.
 */
import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { toCss } from '../web/styleUtils';
import VectorIcon from './Icon';

/** Strips scripts, event-handler attributes and unsafe URLs from rich-text HTML. */
export const sanitizeHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '');

const SAFE_LINK_PATTERN = /^(https?:|mailto:)/i;

export const actions = {
  setBold: 'bold',
  setItalic: 'italic',
  setUnderline: 'underline',
  setStrikethrough: 'strikeThrough',
  heading1: 'formatBlock:h1',
  heading2: 'formatBlock:h2',
  heading3: 'formatBlock:h3',
  alignLeft: 'justifyLeft',
  alignCenter: 'justifyCenter',
  alignRight: 'justifyRight',
  alignFull: 'justifyFull',
  insertBulletsList: 'insertUnorderedList',
  insertOrderedList: 'insertOrderedList',
  insertLink: 'createLink',
  undo: 'undo',
  redo: 'redo',
} as const;

const ACTION_ICONS: Record<string, string> = {
  bold: 'format_bold',
  italic: 'format_italic',
  underline: 'format_underlined',
  strikeThrough: 'strikethrough_s',
  'formatBlock:h1': 'title',
  'formatBlock:h2': 'title',
  'formatBlock:h3': 'title',
  justifyLeft: 'format_align_left',
  justifyCenter: 'format_align_center',
  justifyRight: 'format_align_right',
  justifyFull: 'format_align_justify',
  insertUnorderedList: 'format_list_bulleted',
  insertOrderedList: 'format_list_numbered',
  createLink: 'link',
  undo: 'undo',
  redo: 'redo',
};

export interface RichEditorHandle {
  setContentHTML: (html: string) => void;
  focusContentEditor: () => void;
  blurContentEditor: () => void;
  insertHTML: (html: string) => void;
  getContentHtml: () => Promise<string>;
  registerToolbar?: (cb: any) => void;
}

export interface RichEditorProps {
  initialContentHTML?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange?: (html: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  editorInitializedCallback?: () => void;
  useContainer?: boolean;
  containerStyle?: any;
  style?: any;
  className?: string;
  [key: string]: any;
}

export const RichEditor = forwardRef<RichEditorHandle, RichEditorProps>(function RichEditor(
  { initialContentHTML, disabled, placeholder, onChange, onFocus, onBlur, editorInitializedCallback, style, className },
  ref,
) {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => ({
    setContentHTML: (html: string) => {
      if (editorRef.current) editorRef.current.innerHTML = sanitizeHtml(html);
    },
    focusContentEditor: () => editorRef.current?.focus(),
    blurContentEditor: () => editorRef.current?.blur(),
    insertHTML: (html: string) => document.execCommand('insertHTML', false, sanitizeHtml(html)),
    getContentHtml: async () => sanitizeHtml(editorRef.current?.innerHTML),
    registerToolbar: () => {},
  }));

  useEffect(() => {
    if (editorRef.current && initialContentHTML != null) {
      editorRef.current.innerHTML = sanitizeHtml(initialContentHTML);
    }
    editorInitializedCallback?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={editorRef}
      className={className}
      contentEditable={!disabled}
      data-placeholder={placeholder}
      suppressContentEditableWarning
      onInput={() => onChange?.(sanitizeHtml(editorRef.current?.innerHTML))}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        minHeight: 120,
        outline: 'none',
        padding: 10,
        border: '1px solid #ccc',
        borderRadius: 4,
        background: disabled ? '#f5f5f5' : '#fff',
        ...(toCss(style) as any),
      }}
    />
  );
});

export interface RichToolbarProps {
  editor?: React.RefObject<RichEditorHandle>;
  actions?: string[];
  disabled?: boolean;
  iconTint?: string;
  selectedIconTint?: string;
  disabledIconTint?: string;
  style?: any;
  [key: string]: any;
}

export const RichToolbar = function RichToolbar({
  editor,
  actions: toolbarActions = [],
  disabled,
  iconTint = '#00A3E0',
  disabledIconTint = '#c2c2c2',
  style,
}: RichToolbarProps) {
  const run = (action: string) => {
    if (disabled) return;
    editor?.current?.focusContentEditor();
    const [cmd, arg] = action.split(':');
    if (cmd === 'createLink') {
      const url = window.prompt('Enter link URL')?.trim();
      if (!url) return;
      if (!SAFE_LINK_PATTERN.test(url)) {
        window.alert('Only http, https and mailto links are allowed.');
        return;
      }
      document.execCommand('createLink', false, url);
      return;
    }
    document.execCommand(cmd, false, arg);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: 6,
        background: '#f6f6f6',
        borderRadius: 4,
        ...(toCss(style) as any),
      }}
    >
      {toolbarActions.map((action) => (
        <button
          key={action}
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            run(action);
          }}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            padding: 4,
            borderRadius: 4,
          }}
        >
          <VectorIcon name={ACTION_ICONS[action] ?? 'edit'} size={20} color={disabled ? disabledIconTint : iconTint} />
        </button>
      ))}
    </div>
  );
};

export default RichEditor;
